import { defaultPieceRolesFor, getSchema, DEFAULT_BLOCK_STYLE } from "./blockschema"
export { DEFAULT_BLOCK_STYLE }

// Structural roles describe how a fabric participates in the quilt as a whole,
// independent of any block's internal geometry. Block-level fabric assignment
// (which patches it fills) lives on Fabric.slot — a free-form string validated
// against the active schema's patch.default_role values.
export const STRUCTURAL_ROLES = ["binding", "backing", "sashing", "cornerstone", "setting"] as const
export type StructuralRole = typeof STRUCTURAL_ROLES[number]
export type Distribution = "diagonal" | "checkerboard" | "rows" | "cols" | "dithered_cascade" | "rail_fence_alternating" | "pinwheel_spin" | "diagonal_spin" | "barn_raising" | "streak_of_lightning"
// Lattice transform applied to the whole quilt top.
// - "straight": axis-aligned block grid; Layout.rows × cols is the grid.
// - "on_point": same rows × cols block grid, rotated 45° as a unit. The
//   pieced AABB is a square of side (rows+cols)*blockDiag/2 — the rotated
//   lattice is a tilted diamond inscribed in that square. Setting triangles
//   fill the four canvas corners outside the diamond. Add borders
//   (Layout.border_h_inches / border_v_inches) to extend the square AABB
//   into a rectangular finished blanket. (REC_007 step 2A landed canonical
//   placement; borders are step 2B.)
export type Setting = "straight" | "on_point"

export const SEAM_ALLOWANCE = 0.25
export const FABRIC_WIDTH = 44.0

export type FabricValue = "light" | "medium" | "dark"

// A fabric carries either a structural role (binding/backing/...) OR a block
// slot (a schema-contributed key like "center", "points", "light", "chain").
// The discriminated union enforces the invariant at the type level: exactly
// one of `role` and `slot` is non-null on every Fabric.
interface FabricBase {
  name: string
  group: string | null
  yards: number
  width: number
  value?: FabricValue
  hex?: string | null
}
export type StructuralFabric = FabricBase & { role: StructuralRole; slot: null }
export type SlotFabric = FabricBase & { role: null; slot: string }
export type Fabric = StructuralFabric | SlotFabric

// Construct a Fabric from a loose spec, validating that exactly one of
// role/slot is provided. Throws on violation — call sites that accept
// untrusted input (tool args, persisted state) should catch.
export function makeFabric(spec: {
  name: string
  role?: StructuralRole | null
  slot?: string | null
  group?: string | null
  yards: number
  width?: number
  value?: FabricValue
  hex?: string | null
}): Fabric {
  const role = spec.role ?? null
  const slot = spec.slot ?? null
  if ((role === null) === (slot === null)) {
    throw new Error(`fabric '${spec.name}': exactly one of role/slot is required`)
  }
  const base: FabricBase = {
    name: spec.name,
    group: spec.group ?? null,
    yards: spec.yards,
    width: spec.width ?? FABRIC_WIDTH,
    ...(spec.value !== undefined ? { value: spec.value } : {}),
    ...(spec.hex != null ? { hex: spec.hex } : {}),
  }
  return role !== null
    ? { ...base, role, slot: null }
    : { ...base, role: null, slot: slot as string }
}

export interface Block {
  style: string
  unit_inches: number
  seam: number
  piece_roles: Record<string, string>
}

// Block layout. rows × cols is the block grid (axis-aligned for straight,
// rotated 45° as a unit for on_point — same field meanings either way).
// Under on_point the pieced AABB is square; border_h_inches / border_v_inches
// extend it into a rectangular finished blanket via solid-strip borders.
export interface Layout {
  rows: number
  cols: number
  distribution: Distribution
  dither_swaps: number
  dither_seed: number
  sashing_width: number  // finished sashing strip width in inches; 0 = no sashing
  setting: Setting       // "straight" (default) | "on_point"
  border_h_inches: number  // border thickness on each of left/right (finished inches); on_point only; 0 = no border
  border_v_inches: number  // border thickness on each of top/bottom (finished inches); on_point only; 0 = no border
  alternate_schema?: string  // when set, odd-parity cells (r+c)%2===1 use this schema
  alternate_piece_roles?: Record<string, string>  // piece-role overrides for the alternate block
  cell_schemas?: Record<string, string>  // "r,c" → schemaName; per-cell overrides checked before alternate_schema
}

export interface Project {
  name: string
  fabrics: Fabric[]
  block: Block
  layout: Layout
  colorways: Record<string, Record<string, string>>
}

export function defaultPieceRoles(style: string = DEFAULT_BLOCK_STYLE): Record<string, string> {
  return defaultPieceRolesFor(getSchema(style))
}

export function isDefaultPieceRoles(roles: Record<string, string>, style: string): boolean {
  const defaults = defaultPieceRoles(style)
  return Object.entries(defaults).every(([k, v]) => roles[k] === v)
}

// Look up the first fabric with the given structural role, or null when absent.
export function findFabricByRole(project: Project, role: StructuralRole): Fabric | null {
  return project.fabrics.find((f) => f.role === role) ?? null
}

// Look up the first fabric assigned to the given block slot, or null when absent.
export function findFabricBySlot(project: Project, slot: string): Fabric | null {
  return project.fabrics.find((f) => f.slot === slot) ?? null
}

// Look up a fabric by its (unique) name, or null when absent. Fabric names are
// the user-visible identifier and the join key on CutBatch / FabricUsage, so
// most cross-module lookups go through here.
export function findFabricByName(project: Project, name: string): Fabric | null {
  return project.fabrics.find((f) => f.name === name) ?? null
}

export function defaultBlock(): Block {
  return {
    style: DEFAULT_BLOCK_STYLE,
    unit_inches: 2.0,
    seam: SEAM_ALLOWANCE,
    piece_roles: defaultPieceRoles(),
  }
}

export function defaultLayout(): Layout {
  return { rows: 0, cols: 0, distribution: "diagonal", dither_swaps: 0, dither_seed: 42, sashing_width: 0, setting: "straight", border_h_inches: 0, border_v_inches: 0 }
}

export function defaultProject(): Project {
  return {
    name: "New Quilt",
    fabrics: [],
    block: defaultBlock(),
    layout: defaultLayout(),
    colorways: {},
  }
}

export function toDict(p: Project): Record<string, unknown> {
  const d: Record<string, unknown> = {
    name: p.name,
    fabrics: p.fabrics,
    block: p.block,
    layout: p.layout,
  }
  if (p.colorways && Object.keys(p.colorways).length > 0) d.colorways = p.colorways
  return d
}

export function fromDict(data: Record<string, unknown>): Project {
  type StoredFabric = Partial<FabricBase> & { name: string; role?: StructuralRole | null; slot?: string | null }
  return {
    name: data.name as string,
    fabrics: ((data.fabrics as StoredFabric[] | undefined) ?? []).map((f) =>
      makeFabric({
        name: f.name,
        role: f.role,
        slot: f.slot,
        group: f.group,
        yards: f.yards ?? 0,
        width: f.width,
        value: f.value,
        hex: f.hex,
      })
    ),
    block: { ...defaultBlock(), ...(data.block as Partial<Block> | undefined) },
    layout: { ...defaultLayout(), ...(data.layout as Partial<Layout> | undefined) },
    colorways: (data.colorways as Record<string, Record<string, string>> | undefined) ?? {},
  }
}
